<template>
  <v-card>
    <v-list-item
        dark
        class="primary"
    >
      <v-list-item-avatar>
        <v-icon>
          mdi-file
        </v-icon>
      </v-list-item-avatar>
      <v-list-item-content>
        <v-list-item-title class="headline">Documents</v-list-item-title>
      </v-list-item-content>
      <v-list-item-action>
        <v-btn icon @click.prevent="minimize = !minimize">
          <v-icon v-if="!minimize">mdi-chevron-up</v-icon>
          <v-icon v-else>mdi-chevron-down</v-icon>
        </v-btn>
      </v-list-item-action>
    </v-list-item>
    <v-sheet class="documents-search pa-4 primary lighten-1" v-if="!minimize">
      <v-text-field
          v-model="search"
          label="Search document by name"
          dark
          flat
          solo-inverted
          hide-details
          clearable
          clear-icon="mdi-close"
      >
        <template v-slot:append-outer>
          <documents-upload-dialog :id="id" @success="fetchData"></documents-upload-dialog>
          <v-btn
              v-if="isAdmin"
              dark
              large
              icon
              text
              title="Download All"
              :loading="loadingDownloadAll"
              @click.prevent="downloadAllDocuments()"
          >
            <v-icon>mdi-cloud-download</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </v-sheet>

    <v-card-text v-if="!minimize">
      <v-treeview
          :items="items"
          :search="search"
          :open.sync="open"
          hoverable
          open-on-click
      >
        <template v-slot:prepend="{ item }">
          <v-badge
              v-if="item.children"
              overlap
              class="align-self-center"
              color="indigo lighten-2"
          >
            <template v-slot:badge>
              <span>{{ item.children.length }}</span>
            </template>
            <v-icon
                color="grey"
            >
              mdi-folder
            </v-icon>
          </v-badge>
          <v-icon
              v-else
              v-text="`mdi-file`"
          ></v-icon>
        </template>
        <template v-slot:label="{ item }">
          <div
              class="pl-3 filedrop"
              :class="activeDropFolder === item.id ? 'filedrop--active' : ''"
              @drop="handleDrop($event, item)"
              @dragover="handleDragover($event, item)"
              @dragleave="handleDragleave($event, item)"
          >
            <span>{{ item.name }}</span>
            <v-chip
                class="ml-1"
                v-if="item.applicant"
                small
            >
              <v-icon left small>mdi-account</v-icon>
              {{ item.applicant_name }}
            </v-chip>
            <v-progress-linear
                v-if="activeUploads[item.id]"
                color="teal"
                :buffer-value="activeUploads[item.id].progress"
                :value="activeUploads[item.id].progress"
                absolute
                stream
            ></v-progress-linear>
          </div>
        </template>
        <template v-slot:append="{ item }">
          <v-btn
              icon
              v-if="!item.children"
              @click.stop="deleteDocument(item)"
          >
            <v-icon>mdi-delete</v-icon>
          </v-btn>
          <v-btn
              icon
              v-if="!item.children"
              target="_blank"
              :to="item.file"
              :download="item.name"
          >
            <v-icon>mdi-cloud-download</v-icon>
          </v-btn>
        </template>
      </v-treeview>
    </v-card-text>
  </v-card>
</template>
<script>
import DocumentsUploadDialog from './UploadDialog'
import {mapGetters} from 'vuex'
import {errorNotification} from "@/components/utils";

export default {
  components: {
    DocumentsUploadDialog
  },
  name: 'ApplicationDocuments',
  props: {
    id: Number
  },
  data() {
    return {
      minimize: false,
      search: '',
      documents: [],
      folders: [],
      open: [],
      activeDropFolder: false,
      activeUploads: {},
      loadingDownloadAll: false
    }
  },
  computed: {
    ...mapGetters('auth', [
      'isAdmin'
    ]),
    items() {
      const documents = this.documents
      let root = documents.filter((item) => item.folder === null)
      let folders = []
      // Adding folders to root
      this.folders.forEach((folder) => {
        if (folder.parent == null) {
          folders.push({
            id: 'f_' + folder.id,
            name: folder.name,
            children: documents.filter((item) => item.folder === folder.id)
          })
        }
      })
      // Adding level 2 subfolders
      this.folders.forEach((folder) => {
        if (folder.parent) {
          let parentIndex = folders.findIndex(f => f.id === 'f_' + folder.parent)
          if (!('children' in folders[parentIndex])) {
            folders[parentIndex]['children'] = []
          }
          folders[parentIndex]['children'].push({
            id: 'f_' + folder.id,
            name: folder.name,
            children: documents.filter((item) => item.folder === folder.id)
          })
        }
      })
      return root.concat(folders)
    }
  },
  methods: {
    fetchFolders() {
      this.$axios.get('/applications/' + this.id + '/folders/')
          .then((response) => {
            this.folders = response.data
          })
          .catch((error) => {
            errorNotification(error)
          })
    },
    fetchData() {
      this.$axios.get('/applications/' + this.id + '/documents/', {})
          .then((response) => {
            this.documents = response.data
          })
          .catch((error) => {
            errorNotification(error)
          })
    },
    deleteDocument(document) {
      this.$root.$confirm('Delete', 'Are you sure you want to delete this document?', {color: 'red'}).then((confirm) => {
        if (confirm) {
          this.$axios.delete('/application/documents/' + document.id + '/', {})
              .then(() => {
                let index = this.documents.indexOf(document)
                this.documents.splice(index, 1)
              })
              .catch((error) => {
                errorNotification(error)
              })
        }
      })
    },
    handleDrop(e, folder) {
      this.activeDropFolder = false
      let files = []
      if (e.dataTransfer.items) {
        // Use DataTransferItemList interface to access the file(s)
        for (let i = 0; i < e.dataTransfer.items.length; i++) {
          // If dropped items aren't files, reject them
          if (e.dataTransfer.items[i].kind === 'file') {
            let file = e.dataTransfer.items[i].getAsFile();
            files.push(file)
          }
        }
      } else {
        // Use DataTransfer interface to access the file(s)
        for (let i = 0; i < e.dataTransfer.files.length; i++) {
          files.push(e.dataTransfer.files[i])
        }
      }
      const folderFiles = folder.children.map((i) => i.name)
      const uploadFiles = files.map((i) => i.name)
      let intersection = folderFiles.filter(i => uploadFiles.includes(i))
      if (intersection.length) {
        let message = `Folder "${folder.name}" already contains file${intersection.length > 1 ? 's' : ''}: ${intersection.join(', ')}. Overwrite?`
        this.$root.$confirm('Overwrite', message, {color: 'red'}).then((confirm) => {
          if (confirm) {
            this.handleUpload(files, folder.id.replace('f_', ''))
          }
        })
      } else {
        this.handleUpload(files, folder.id.replace('f_', ''))
      }
    },
    handleDragover(e, folder) {
      this.activeDropFolder = folder.id
    },
    handleDragleave(e, folder) {
      if (this.activeDropFolder === folder.id) {
        this.activeDropFolder = false
      }
    },
    handleUpload(files, folder) {
      this.activeUploadBegin(folder, files.length)
      files.forEach((file) => {
        let data = new FormData()
        data.append('application', this.id)
        // if (this.applicant) {
        //     data.append('applicant', this.applicant)
        // }
        if (folder) {
          data.append('folder', folder)
        }
        data.append('name', file.name)
        data.append('file', file)
        this.uploading = true
        this.$axios.post('/application/documents/', data)
            .then(() => {
              this.fetchData()
            })
            .catch((error) => {
              errorNotification(error)
            })
            .finally(() => {
              this.activeUploadComplete(folder)
            })
      })
    },
    activeUploadBegin(id, total) {
      let prefixedId = 'f_' + id
      this.activeUploads[prefixedId] = {
        total: total,
        completed: 0,
        progress: 0
      }
    },
    activeUploadComplete(id) {
      let prefixedId = 'f_' + id
      let details = this.activeUploads[prefixedId]
      if (details) {
        details.completed += 1
        details.progress = 100 * details.completed / details.total
      }
      if (details.completed === details.total) {
        delete this.activeUploads[prefixedId]
      }
    },
    downloadAllDocuments() {
      this.loadingDownloadAll = true
      this.$axios.get('/applications/' + this.id + '/documents_download/', {
        responseType: 'blob'
      })
          .then((response) => {
            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `documents_${this.id}.zip`)
            document.body.appendChild(link)
            link.click()
          })
          .catch((error) => {
            errorNotification(error)
          })
          .finally(() => {
            this.loadingDownloadAll = false
          })
    }
  },
  mounted() {
    this.fetchFolders()
    this.fetchData()
    // Prevent browser to load dragged and dropped file
    window.addEventListener('dragover', function (e) {
      e = e || event;
      e.preventDefault();
    }, false);
    window.addEventListener('drop', function (e) {
      e = e || event;
      e.preventDefault();
    }, false);
  }
}
</script>
<style rel="stylesheet/scss" lang="scss">
.documents-search {
  border-top-left-radius: 0;
  border-top-right-radius: 0;

  .v-input__append-outer {
    margin-top: 4px !important;
  }
}

.filedrop {
  min-height: 48px;
  display: flex;
  flex: 1;
  line-height: 48px;

  &--active {
    font-weight: bold;
    background: #e6fdfd;
    border-radius: 3px;
    color: #4a6f6f;
  }
}
</style>